import React from 'react';
import { Loading, SystemText } from '@salesperformanceint/e4shared';
import { useSelector } from 'react-redux';

import Dropdown from 'components/general/dropdown';
import BehaviourCard from 'components/prism/behaviourCard';
import Metrics from 'components/prism/metrics';
import NoResults from 'components/prism/noResults';
import Subheader from 'components/prism/subheader';
import { apiGet } from 'services';
import ManageTask from 'views/user/components/manageTask';

import { Overtime } from './components/overtime';

export const Overview = ({ profile }) => {
    const [loading, setLoading] = React.useState(true);
    const [loadingOvertime, setLoadingOvertime] = React.useState(true);
    const [loadingMetrics, setLoadingMetrics] = React.useState(true);
    const [data, setData] = React.useState({});
    const [overtimeData, setOvertimeData] = React.useState([]);
    const [opened, setOpened] = React.useState(0);
    const [metric, setMetric] = React.useState(null);
    const [period, setPeriod] = React.useState('M');
    const [showManageTask, setShowManageTask] = React.useState(false);
    const { prismMultiColumn } = useSelector(state => state.ui);
    const { user } = useSelector(state => state.user);

    React.useEffect(() => {
        if (profile?.id) {
            setLoading(true);
            setLoadingMetrics(true);
            apiGet(`team-member/overview/${profile.id}?period=${period}`, user.guid, response => {
                setData(response);
                setLoading(false);
                setLoadingMetrics(false);
            });
        }
    }, [profile?.id]);

    React.useEffect(() => {
        setLoadingMetrics(true);
        if (profile?.id && !loading) {
            apiGet(`team-member/overview/${profile.id}?metrics=true&period=${period}`, user.guid, response => {
                setData({ ...data, metrics: response.metrics });
                setLoadingMetrics(false);
            });
        }
    }, [period]);

    React.useEffect(() => {
        if (data?.metrics?.length) {
            setMetric(data.metrics[0]);
        }
    }, [data?.metrics]);

    React.useEffect(() => {
        setLoadingOvertime(true);
        if (profile?.id && !loading) {
            const url =
                metric.type === 'CRM'
                    ? `crm-over-time/${profile.id}/${metric.id}`
                    : `integration-overtime/${profile.id}/${metric.id}`;
            apiGet(url, user.guid, response => {
                setOvertimeData(response);
                setLoadingOvertime(false);
            });
        }
    }, [metric]);

    const toggleOpened = id => {
        setOpened(prev => {
            return id === prev ? 0 : id;
        });
    };

    const text = {
        behaviours: {
            title: SystemText('TeamMember/Overview/Behaviour/Title', profile.first_Name),
            noResultsTitle: SystemText('TeamMember/Overview/Behaviour/NoResultsTitle', profile.first_Name),
            noResultsSummary: SystemText('TeamMember/Overview/Behaviour/NoResultsSummary'),
        },
        keyMetrics: SystemText('TeamMember/Overview/KeyMetrics'),
        selectMetric: SystemText('TeamMember/Overview/SelectMetric'),
    };

    return (
        <div className="mt20">
            {loading ? (
                <Loading />
            ) : (
                <>
                    <Subheader title={text.behaviours.title} />
                    {data.behaviours.length === 0 && (
                        <div>
                            <NoResults
                                icon="times-circle"
                                title={text.behaviours.noResultsTitle}
                                summary={text.behaviours.noResultsSummary}
                            />
                        </div>
                    )}
                    {data.behaviours.map(x => {
                        return (
                            <BehaviourCard
                                profile={profile}
                                behaviour={x}
                                opened={opened === x.id}
                                setOpened={toggleOpened}
                                setShowManageTask={setShowManageTask}
                            />
                        );
                    })}
                    {data?.metrics?.length > 0 && (
                        <div className={`${prismMultiColumn ? 'two-col mt30' : ''}`}>
                            <div className={`${!prismMultiColumn ? 'mt30' : ''}`}>
                                <Metrics
                                    title={text.keyMetrics}
                                    data={data?.metrics.slice(0, 6)}
                                    period={period}
                                    setPeriod={setPeriod}
                                    loading={loadingMetrics}
                                />
                            </div>
                            <div className={`${!prismMultiColumn ? 'mt30' : ''}`}>
                                <Subheader
                                    title="Metric chart"
                                    h2class="invisible"
                                    extra={
                                        <Dropdown
                                            label={metric?.name || text.selectMetric}
                                            anchorRight
                                            options={data?.metrics.map(x => {
                                                return {
                                                    name: x.name,
                                                    value: x.id,
                                                    onClick: () => setMetric(x),
                                                };
                                            })}
                                        />
                                    }
                                />
                                <div className="prism-content-box">
                                    <div className="prism-content" style={{ height: '422px' }}>
                                        {loadingOvertime ? <Loading /> : <Overtime data={overtimeData} metric={metric} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
            <ManageTask
                show={showManageTask}
                defaultCompetency={showManageTask}
                values={{ profile_Ids: [profile.id] }}
                close={() => setShowManageTask(false)}
            />
        </div>
    );
};

export default Overview;
